/**
 * Building and checking a pre-race stint plan.
 *
 * The generator is a greedy scheduler rather than a search, because the
 * regulations leave a lot of slack: a 25h PRO race must make 30 stops where the
 * stint cap alone would force only 21, so the average stint runs about 24
 * minutes under the cap. That slack is what pays for sliding a stop clear of a
 * closed pit lane, and it is what the stint profile spends when you ask for
 * maximum stint time.
 *
 * What the profile cannot do is change how *many* stops there are. Rule 10 fixes
 * the count, so the number of stints and the total driving time are fixed with
 * it — every minute added to one stint comes off another.
 *
 * Pass A lays out the lengths, pass B hands them to drivers, and passes C1/C2
 * repair the per-driver totals: first by reassigning whole stints, which leaves
 * the timeline alone, and only then by moving minutes, which does not.
 * `stintPlan.test.ts` asserts the result is legal across every combination of
 * duration, category, track, direction change, profile, double-stinting and
 * squad size.
 */
import {
  ClosedPitWindow,
  DerivedConstraints,
  LATE_STOP_CONCENTRATION,
  StintBias,
  closureAt,
  deriveConstraints,
} from './regulations';
import { PlannedStint, StrategyConfig, StrategyDriver, StrategyPlan } from '../types';

/** Minutes below which two plan times are treated as equal. */
const EPSILON = 1e-6;

/** Bound on pass C so a plan that cannot balance reports instead of hanging. */
const MAX_BALANCE_STEPS = 500;

/**
 * Where a partly-run plan picks up. Everything before `index` has already
 * happened and is fixed; the stint at `index` began at `startMinute`, which is
 * a measurement rather than the sum of the planned lengths before it.
 */
export interface TailAnchor {
  index: number;
  startMinute: number;
}

/** Options letting a repair pass leave the stints already run alone. */
interface RepairScope {
  /** First stint index the pass may change. Earlier ones are history. */
  mutableFrom?: number;
  /** Where to start re-summing times from; defaults to the race start. */
  from?: TailAnchor;
}

export interface Violation {
  /** Index into `stints`, or null when the violation is about the whole plan. */
  stintIndex: number | null;
  /** The regulation clause, e.g. '14b' — shown so a steward can be argued with. */
  rule: string;
  severity: 'error' | 'warning';
  message: string;
  /** The penalty the regulations attach, when there is one. */
  penalty: string | null;
}

export interface DriverTotal {
  name: string;
  driverIndex: number;
  minutes: number;
  stints: number;
  /** Null when the regulations set no bound for this squad size. */
  minMinutes: number | null;
  maxMinutes: number | null;
  shortfallMinutes: number;
}

export interface PlanSummary {
  driverTotals: DriverTotal[];
  totalDrivingMinutes: number;
  totalPitMinutes: number;
  plannedMinutes: number;
  stops: number;
  driverChanges: number;
  kartOnlyStops: number;
  constraints: DerivedConstraints;
}

function driverCountOf(roster: StrategyDriver[]): number {
  return roster.filter((d) => !d.unavailable).length;
}

/**
 * Rule 6 — squad size. Checked from both `generatePlan` and `validatePlan`: the
 * generator wants to refuse early, but a plan can also arrive already built, or
 * lose a driver after the fact, and must still say so.
 */
function rosterViolations(
  availableCount: number,
  c: DerivedConstraints,
  durationHours: number,
): Violation[] {
  if (availableCount === 0) {
    return [
      {
        stintIndex: null,
        rule: '6',
        severity: 'error',
        message: 'Add at least one available driver before generating a plan.',
        penalty: null,
      },
    ];
  }
  const out: Violation[] = [];
  if (availableCount < c.minDrivers) {
    out.push({
      stintIndex: null,
      rule: '6',
      severity: 'error',
      message: `A ${durationHours}-hour race needs at least ${c.minDrivers} drivers; ${availableCount} available.`,
      penalty: null,
    });
  }
  if (availableCount > c.maxDrivers) {
    out.push({
      stintIndex: null,
      rule: '6',
      severity: 'error',
      message: `A ${durationHours}-hour race allows at most ${c.maxDrivers} drivers; ${availableCount} available.`,
      penalty: null,
    });
  }
  return out;
}

/** Indexes of the drivers auto-assignment may use, in roster order. */
function availableIndexes(roster: StrategyDriver[]): number[] {
  const out: number[] = [];
  roster.forEach((d, i) => {
    if (!d.unavailable) out.push(i);
  });
  return out;
}

/**
 * The windows the generator steers around — the closures widened by the safety
 * margin, all of them, including the one over the last minutes of the race. Only
 * the final *stint* is exempt, because it runs to the flag and never pits; the
 * stop before it is as illegal in that window as anywhere else. An even split
 * never came close to the end closure, so that only starts to bite once a
 * front-loaded profile leaves short stints in the tail.
 */
function pitWindowsOf(c: DerivedConstraints): ClosedPitWindow[] {
  return c.planningPitWindows;
}

/**
 * Whether every stop in a set of lengths finds the pit lane genuinely open.
 *
 * Checked against the regulation closures, not the widened ones: this gates the
 * rollback in pass C2, and refusing a repair merely for landing inside the
 * safety margin would block plans that are perfectly legal.
 */
function stopsAreLegal(
  lengths: number[],
  c: DerivedConstraints,
  from: TailAnchor = { index: 0, startMinute: 0 },
): boolean {
  const windows = c.closedPitWindows;
  let at = from.startMinute;
  for (let i = from.index; i < lengths.length; i += 1) {
    at += lengths[i];
    if (i === lengths.length - 1) break;
    if (closureAt(at, windows)) return false;
    at += c.minPitMinutes;
  }
  return true;
}

/**
 * A stop that is legal but sits inside the safety margin.
 *
 * Reported rather than silently accepted: the plan is not illegal, but a closure
 * that starts a minute early — cones out, a message posted, a safety kart — turns
 * it into three laps. The generator never produces one; this catches hand edits.
 */
function marginWarning(
  pitIn: number,
  stintIndex: number,
  c: DerivedConstraints,
): Violation | null {
  const margin = c.pitMarginMinutes;
  if (margin <= 0) return null;

  const near = c.closedPitWindows.find(
    (w) =>
      (pitIn < w.fromMinute && pitIn >= w.fromMinute - margin) ||
      (pitIn >= w.toMinute && pitIn < w.toMinute + margin),
  );
  if (!near) return null;

  const before = pitIn < near.fromMinute;
  const gap = before ? near.fromMinute - pitIn : pitIn - near.toMinute;
  const rounded = Math.round(gap * 10) / 10;

  return {
    stintIndex,
    rule: '16',
    severity: 'warning',
    message: before
      ? `Pits ${rounded} min before the lane shuts (${near.reason.toLowerCase()}) — the plan keeps ${margin}.`
      : `Pits ${rounded} min after the lane reopens (${near.reason.toLowerCase()}) — the plan keeps ${margin}.`,
    penalty: null,
  };
}

/**
 * The stint length nearest `want` that puts its Pit In outside `windows`, or
 * null when no length in `[lower, upper]` can.
 *
 * Two ways out of a closure: hold out until the lane reopens, or come in before
 * it shuts. Pitting *on* the opening minute still counts as closed, which is why
 * the early option floors rather than rounds.
 */
function lengthClearOf(
  elapsed: number,
  want: number,
  lower: number,
  upper: number,
  windows: ClosedPitWindow[],
): number | null {
  const start = Math.max(lower, Math.min(upper, want));
  if (!closureAt(elapsed + start, windows)) return start;

  const shut = closureAt(elapsed + start, windows)!;
  const hold = Math.ceil(shut.toMinute - elapsed - EPSILON);
  if (hold >= lower && hold <= upper && !closureAt(elapsed + hold, windows)) {
    return hold;
  }
  const early = Math.floor(shut.fromMinute - elapsed - EPSILON);
  if (early >= lower && early <= upper && !closureAt(elapsed + early, windows)) {
    return early;
  }
  return null;
}

/** Relative weight per stint — which the profile wants longest, in order. */
function biasShape(n: number, bias: StintBias): number[] {
  if (bias === 'even' || n <= 1) return new Array(n).fill(1);
  return Array.from({ length: n }, (_, i) => (bias === 'front' ? n - i : i + 1));
}

/**
 * Maximum stint time: fill stints to the cap in weight order until the quota or
 * the budget runs out, then share what is left evenly.
 *
 * Worth being blunt about what this can and cannot do. The stop count is fixed
 * by rule 10, so the number of stints and the total driving time are fixed too —
 * every minute added to an early stint is taken off a later one. A 25-hour PRO
 * race can hold 18 of its 31 stints at the 70-minute cap, and the other 13 then
 * sit near the 10-minute floor.
 */
function fillToCap(
  shape: number[],
  total: number,
  lo: number,
  hi: number,
  quota: number | null,
): number[] {
  const n = shape.length;
  const order = shape.map((_, i) => i).sort((a, b) => shape[b] - shape[a]);
  const allowed = quota === null ? n : Math.max(0, Math.min(n, quota));

  const out = new Array(n).fill(lo);
  let left = total - n * lo;
  const capped = new Set<number>();

  for (const i of order) {
    // Only count a stint as capped when it can be filled all the way; a partial
    // fill would eat the quota without buying a full-length stint.
    if (capped.size >= allowed || left < hi - lo - EPSILON) break;
    out[i] += hi - lo;
    left -= hi - lo;
    capped.add(i);
  }

  const rest = out.map((_, i) => i).filter((i) => !capped.has(i));
  if (rest.length > 0 && left > EPSILON) {
    const share = left / rest.length;
    rest.forEach((i) => {
      out[i] = Math.min(hi, lo + share);
    });
    // Clamping can leave a few minutes stranded; hand them back out.
    let residue = total - out.reduce((s, x) => s + x, 0);
    for (const i of rest) {
      if (residue <= EPSILON) break;
      const add = Math.min(hi - out[i], residue);
      out[i] += add;
      residue -= add;
    }
  }

  return out;
}

/** Water-fill: keep the shape's proportions, clamped into [lo, hi]. */
function fillProportional(
  shape: number[],
  total: number,
  lo: number,
  hi: number,
): number[] {
  const n = shape.length;
  const out: (number | null)[] = new Array(n).fill(null);
  const free = new Set(shape.map((_, i) => i));
  let left = total;

  for (let guard = 0; guard < 60; guard += 1) {
    const weight = Array.from(free).reduce((s, i) => s + shape[i], 0);
    if (weight <= EPSILON) break;

    let clamped = false;
    for (const i of Array.from(free)) {
      const v = (left * shape[i]) / weight;
      if (v < lo - EPSILON) {
        out[i] = lo;
        left -= lo;
        free.delete(i);
        clamped = true;
      } else if (v > hi + EPSILON) {
        out[i] = hi;
        left -= hi;
        free.delete(i);
        clamped = true;
      }
    }
    if (clamped) continue;

    // Snapshot before the closure: `left` is still being written above.
    const remaining = left;
    const rest = Array.from(free).reduce((s, i) => s + shape[i], 0);
    free.forEach((i) => {
      out[i] = (remaining * shape[i]) / rest;
    });
    break;
  }

  return out.map((v) => (v === null ? lo : v));
}

/**
 * Pass A — stint lengths.
 *
 * Lay out a target profile, then walk it stint by stint pushing each Pit In
 * clear of a closed lane: hold out until it reopens if the cap allows, else come
 * in strictly before it shuts. Pitting *on* the opening minute still counts as
 * closed, which is why the early option floors rather than rounds.
 *
 * Every planned stint lands on a whole minute. The bounds are made integral
 * before clamping, because the driving budget itself is fractional (135-second
 * stops are 2.25 minutes) and a fractional clamp would reintroduce decimals.
 * The final stint is the exception: it runs to the flag, so it carries whatever
 * remainder is left.
 */
function planStintLengths(c: DerivedConstraints, config: StrategyConfig): number[] {
  const { requiredStops, raceMinutes, minPitMinutes } = c;
  const n = requiredStops + 1;
  const driving = raceMinutes - requiredStops * minPitMinutes;
  const lo = c.planningFloorMinutes;
  const hi = c.maxStintMinutes;
  const windows = pitWindowsOf(c);

  /**
   * How long the run to the flag has to be for the stop *before* it to clear the
   * end-of-race closure. Without reserving this up front, a front-loaded profile
   * spends the budget early and arrives at the last stop with nowhere legal to
   * put it — the tail is too short to pit before the lane shuts and too short to
   * skip the stop. Half a minute of margin keeps it clear of the boundary.
   */
  const endClosure = c.planningPitWindows[c.planningPitWindows.length - 1];
  const finalStintFloor = Math.max(
    lo,
    raceMinutes - endClosure.fromMinute - minPitMinutes + 0.5,
  );

  const shape = biasShape(n, config.stintBias);
  const quota = c.maxStintsAtCap;
  const refill = (slice: number[], budget: number, left: number | null): number[] =>
    config.maximiseStintTime && config.stintBias !== 'even'
      ? fillToCap(slice, budget, lo, hi, left)
      : fillProportional(slice, budget, lo, hi);

  let targets = refill(shape, driving, quota);
  const lengths: number[] = [];
  let elapsed = 0;
  let atCap = 0;

  for (let i = 0; i < n; i += 1) {
    const cap = i === 0 ? c.maxFirstStintMinutes : hi;
    const stintsAfter = n - i - 1;
    const budget = driving - lengths.reduce((s, x) => s + x, 0);

    let minutes: number;
    if (stintsAfter === 0) {
      minutes = budget;
    } else {
      // Integral bounds keep the tail feasible *and* the answer whole. The tail
      // reserve is `lo` for each stint except the run to the flag, which needs
      // enough length to keep the stop before it out of the end closure.
      const reserve = (stintsAfter - 1) * lo + finalStintFloor;
      const upper = Math.floor(Math.min(cap, budget - reserve) + EPSILON);
      const lower = Math.ceil(Math.max(lo, budget - stintsAfter * hi) - EPSILON);
      const want = Math.ceil(Math.min(Math.max(targets[i], lo), cap) - EPSILON);

      // Keep the safety margin where it fits. Where it cannot — a back-loaded
      // profile can reach a stint whose every legal length lands inside the
      // widened midpoint window — fall back to the closure as written. The
      // margin is prudence; staying legal is not optional.
      minutes =
        lengthClearOf(elapsed, want, lower, upper, windows) ??
        lengthClearOf(elapsed, want, lower, upper, c.closedPitWindows) ??
        Math.max(lower, Math.min(upper, want));
    }

    lengths.push(minutes);
    if (minutes >= hi - EPSILON) atCap += 1;
    elapsed += minutes + (stintsAfter > 0 ? minPitMinutes : 0);

    if (stintsAfter > 0) {
      const spent = lengths.reduce((s, x) => s + x, 0);
      // The quota is spent as it is used — refilling the tail with the original
      // allowance would let it reset and creep past rule 18.
      targets = [
        ...lengths,
        ...refill(
          shape.slice(i + 1),
          driving - spent,
          quota === null ? null : Math.max(0, quota - atCap),
        ),
      ];
    }
  }

  return lengths;
}

/** Rule 11 — nobody may exceed their consecutive-stint allowance. */
function runsAreLegal(assigned: number[], limit: number): boolean {
  let run = 1;
  for (let i = 1; i < assigned.length; i += 1) {
    run = assigned[i] === assigned[i - 1] ? run + 1 : 1;
    if (run > limit) return false;
  }
  return true;
}

function totalsOf(
  lengths: number[],
  assigned: number[],
  usable: number[],
): Map<number, number> {
  const totals = new Map<number, number>(usable.map((i) => [i, 0]));
  assigned.forEach((d, i) => {
    if (totals.has(d)) totals.set(d, (totals.get(d) ?? 0) + lengths[i]);
  });
  return totals;
}

/**
 * Pass B — who drives what.
 *
 * At each stint take whoever is furthest below their minimum, skipping the
 * current driver once they have used up their consecutive-stint allowance.
 *
 * With `preferDoubleStints` the current driver is kept for a second stint
 * wherever rule 11c allows it — PRO and GOLD may run two in a row and change
 * only the kart, which keeps the driver in the seat and makes the stop a
 * straight kart swap. SILVER cannot: rule 11b makes every pit entry a driver
 * change, so the flag has no effect there.
 */
function assignDrivers(
  lengths: number[],
  c: DerivedConstraints,
  roster: StrategyDriver[],
  preferDoubleStints: boolean,
): (number | null)[] {
  const usable = availableIndexes(roster);
  if (usable.length === 0) return lengths.map(() => null);

  const min = c.minDriverMinutes ?? 0;
  const max = c.maxDriverMinutes;
  const totals = new Map<number, number>(usable.map((i) => [i, 0]));

  const assigned: number[] = [];
  let current = usable[0];
  let run = 0;

  lengths.forEach((minutes, i) => {
    let pick: number;
    if (i === 0) {
      pick = usable[0];
    } else {
      const canDouble =
        preferDoubleStints &&
        c.maxConsecutiveStints > 1 &&
        run < c.maxConsecutiveStints &&
        (max === null || (totals.get(current) ?? 0) + minutes <= max + EPSILON);

      if (canDouble) {
        pick = current;
      } else {
        // With double-stinting off, hand over at every stop rather than merely
        // staying inside rule 11. The levelling below picks whoever has driven
        // least, and after a short stint that can still be the driver already in
        // the kart — which produced a kart-only stop nobody asked for.
        const runLimit = preferDoubleStints ? c.maxConsecutiveStints : 1;
        let candidates = usable.filter((k) => !(k === current && run >= runLimit));
        if (candidates.length === 0) candidates = usable;

        if (max !== null) {
          const fits = candidates.filter(
            (k) => (totals.get(k) ?? 0) + minutes <= max + EPSILON,
          );
          if (fits.length > 0) candidates = fits;
        }

        // Furthest below the minimum first; once everyone is clear, level up.
        pick = candidates.reduce((best, k) => {
          const score = (t: number): number => (t < min ? t - min : 1e6 + t);
          return score(totals.get(k) ?? 0) < score(totals.get(best) ?? 0) ? k : best;
        }, candidates[0]);
      }
    }

    run = pick === current ? run + 1 : 1;
    current = pick;
    totals.set(pick, (totals.get(pick) ?? 0) + minutes);
    assigned.push(pick);
  });

  return assigned;
}

/**
 * Pass C1 — hand whole stints to whoever is short.
 *
 * Tried before moving any minutes because reassigning a stint leaves every
 * length untouched, so the timeline — and every closed-window dodge pass A paid
 * for — survives exactly.
 */
function repairByReassignment(
  lengths: number[],
  assigned: number[],
  c: DerivedConstraints,
  usable: number[],
  scope: RepairScope = {},
): void {
  const mutableFrom = scope.mutableFrom ?? 0;
  const min = c.minDriverMinutes ?? 0;
  const max = c.maxDriverMinutes;
  const limit = c.maxConsecutiveStints;
  const byLength = lengths.map((_, i) => i).sort((a, b) => lengths[b] - lengths[a]);

  for (let step = 0; step < MAX_BALANCE_STEPS; step += 1) {
    const totals = totalsOf(lengths, assigned, usable);
    const short = usable.filter((k) => (totals.get(k) ?? 0) < min - EPSILON);
    const over =
      max === null
        ? []
        : usable.filter((k) => (totals.get(k) ?? 0) > max + EPSILON);
    if (short.length === 0 && over.length === 0) return;

    let moved = false;
    for (const k of short.length > 0 ? short : over) {
      const isShort = short.includes(k);
      for (const j of byLength) {
        if (j < mutableFrom) continue;
        if (isShort) {
          const donor = assigned[j];
          if (donor === k) continue;
          if ((totals.get(donor) ?? 0) - lengths[j] < min - EPSILON) continue;
          if (max !== null && (totals.get(k) ?? 0) + lengths[j] > max + EPSILON) continue;
          const trial = [...assigned];
          trial[j] = k;
          if (!runsAreLegal(trial, limit)) continue;
          assigned[j] = k;
          moved = true;
          break;
        }

        if (assigned[j] !== k) continue;
        if ((totals.get(k) ?? 0) - lengths[j] < min - EPSILON) continue;
        const taker = usable.find(
          (r) =>
            r !== k &&
            (max === null || (totals.get(r) ?? 0) + lengths[j] <= max + EPSILON),
        );
        if (taker === undefined) continue;
        const trial = [...assigned];
        trial[j] = taker;
        if (!runsAreLegal(trial, limit)) continue;
        assigned[j] = taker;
        moved = true;
        break;
      }
      if (moved) break;
    }
    if (!moved) return;
  }
}

/**
 * Pass C2 — move whole minutes between stints.
 *
 * Reassignment cannot always finish the job: with 18 stints across 5 drivers
 * somebody only gets three of them, and three average stints fall short of a
 * two-hour minimum however they are shuffled. Those drivers need *longer*
 * stints, which means moving minutes rather than ownership.
 *
 * Changing a length shifts every start time after it, so a transfer that pushes
 * a stop into a closed pit lane is rolled straight back and the next pair tried.
 * Minutes move whole so the sheet stays in whole minutes.
 */
function repairByMinutes(
  lengths: number[],
  assigned: number[],
  c: DerivedConstraints,
  usable: number[],
  scope: RepairScope = {},
): boolean {
  const mutableFrom = scope.mutableFrom ?? 0;
  const min = c.minDriverMinutes;
  if (min === null || min <= 0) return true;
  const max = c.maxDriverMinutes;
  const lo = c.planningFloorMinutes;

  for (let step = 0; step < MAX_BALANCE_STEPS; step += 1) {
    const totals = totalsOf(lengths, assigned, usable);
    const short = usable.filter((k) => (totals.get(k) ?? 0) < min - EPSILON);
    if (short.length === 0) return true;

    const taker = short.reduce((a, b) =>
      (totals.get(a) ?? 0) <= (totals.get(b) ?? 0) ? a : b,
    );
    const donor = usable
      .filter((k) => k !== taker)
      .reduce<number | null>(
        (best, k) =>
          best === null || (totals.get(k) ?? 0) > (totals.get(best) ?? 0) ? k : best,
        null,
      );
    if (donor === null || (totals.get(donor) ?? 0) - min <= EPSILON) return false;

    let moved = false;
    for (let i = mutableFrom; i < lengths.length && !moved; i += 1) {
      if (assigned[i] !== taker) continue;
      const cap = i === 0 ? c.maxFirstStintMinutes : c.maxStintMinutes;

      for (let j = mutableFrom; j < lengths.length && !moved; j += 1) {
        if (assigned[j] !== donor) continue;

        const need = min - (totals.get(taker) ?? 0);
        let room = Math.min(cap - lengths[i], lengths[j] - lo, (totals.get(donor) ?? 0) - min);
        if (max !== null) {
          room = Math.min(room, Math.max(0, max - (totals.get(taker) ?? 0)));
        }

        let delta: number;
        let smallest: number;
        if (scope.from === undefined) {
          // A fresh layout is all whole minutes, so shortfalls are too.
          delta = Math.floor(Math.min(need, room));
          smallest = 1;
        } else {
          // Mid-race the stints already run are measured, not planned, and a
          // measured total is rarely a whole number: a driver can be a quarter of
          // a minute short, which a whole-minute move can never close. Work to
          // the hundredth, rounding the move up to meet the need and down to
          // respect the room.
          const meets = Math.ceil(need * 100 - 1e-6) / 100;
          const fits = Math.floor(room * 100 + 1e-6) / 100;
          delta = Math.min(meets, fits);
          smallest = 0.01;
        }
        if (delta < smallest) continue;

        lengths[i] += delta;
        lengths[j] -= delta;
        if (stopsAreLegal(lengths, c, scope.from)) {
          moved = true;
        } else {
          lengths[i] -= delta;
          lengths[j] += delta;
        }
      }
    }
    if (!moved) return false;
  }

  return false;
}

export interface StopPressure {
  /** Spare minutes at the tightest point of the plan. Negative means it cannot fit. */
  slackMinutes: number;
  /** The stop where the plan is tightest — index of the stint it follows. */
  atStintIndex: number;
  /** Stops still outstanding at that point, including that one. */
  stopsRemaining: number;
  /** Minute at which the pit lane shuts for the last time. */
  closesAtMinute: number;
  /** How many stops fall after the three-quarter mark. */
  stopsInFinalQuarter: number;
  /** What an evenly spread plan would leave there. */
  evenShareInFinalQuarter: number;
  /** Minute the final quarter begins. */
  finalQuarterFromMinute: number;
}

/**
 * How much room a plan leaves itself to finish its mandatory stops.
 *
 * A stop costs the same wherever it is taken, so back-loading them looks free on
 * paper. It is not: the lane shuts for the last stretch of the race (rule 16d/e),
 * and a safety kart can shut it earlier with no warning at all. Every stop still
 * outstanding has to fit before that, each needing its minimum pit time plus a
 * stint of driving to reach the next one.
 *
 * Measured at every stop, the worst case is the plan's real margin. It is what
 * separates "we took our stops late" from "one yellow flag and we owed 5 laps a
 * stop" — a distinction that only shows up once you count it.
 */
export function stopPressure(
  stints: PlannedStint[],
  c: DerivedConstraints,
): StopPressure | null {
  if (stints.length < 2) return null;

  const closesAt = c.closedPitWindows[c.closedPitWindows.length - 1].fromMinute;
  const stops = stints.length - 1;

  let worst: StopPressure | null = null;
  for (let i = 0; i < stops; i += 1) {
    const pitIn = stints[i].startMinute + stints[i].minutes;
    const remaining = stops - i;
    // This stop, then a stint and a stop for each one after it.
    const needed = remaining * c.minPitMinutes + (remaining - 1) * c.planningFloorMinutes;
    const slack = closesAt - pitIn - needed;
    if (worst === null || slack < worst.slackMinutes) {
      worst = {
        slackMinutes: slack,
        atStintIndex: i,
        stopsRemaining: remaining,
        closesAtMinute: closesAt,
        stopsInFinalQuarter: 0,
        evenShareInFinalQuarter: 0,
        finalQuarterFromMinute: 0,
      };
    }
  }
  if (worst === null) return null;

  const threeQuarter = c.raceMinutes * 0.75;
  worst.finalQuarterFromMinute = threeQuarter;
  worst.evenShareInFinalQuarter = stops / 4;
  worst.stopsInFinalQuarter = stints
    .slice(0, -1)
    .filter((s) => s.startMinute + s.minutes >= threeQuarter).length;
  return worst;
}

/** Rule 11 — SILVER swaps driver every stop; PRO/GOLD may change only the kart. */
export function stopKinds(
  assigned: (number | null)[],
  c: DerivedConstraints,
): PlannedStint['stopKind'][] {
  return assigned.map((driver, i) => {
    if (i === assigned.length - 1) return 'none';
    if (c.mustChangeDriverEveryStop) return 'driver-and-kart';
    const next = assigned[i + 1];
    return next !== null && next === driver ? 'kart-only' : 'driver-and-kart';
  });
}

function toStints(
  lengths: number[],
  assigned: (number | null)[],
  kinds: PlannedStint['stopKind'][],
  minPitMinutes: number,
): PlannedStint[] {
  const stints: PlannedStint[] = [];
  let startMinute = 0;

  lengths.forEach((minutes, i) => {
    stints.push({
      startMinute,
      minutes,
      driverIndex: assigned[i],
      stopKind: kinds[i],
      note: null,
    });
    startMinute += minutes + (i < lengths.length - 1 ? minPitMinutes : 0);
  });

  return stints;
}

/**
 * Recompute what each stop changes from who drives either side of it. Needed
 * after a hand edit: swapping a stint's driver turns a kart-only stop into a
 * driver change (or back), and the sheet's counts depend on it.
 */
export function withStopKinds(
  stints: PlannedStint[],
  config: StrategyConfig,
  roster: StrategyDriver[],
): PlannedStint[] {
  const c = deriveConstraints(config, driverCountOf(roster));
  const kinds = stopKinds(
    stints.map((s) => s.driverIndex),
    c,
  );
  return stints.map((s, i) => (s.stopKind === kinds[i] ? s : { ...s, stopKind: kinds[i] }));
}

export interface RebalanceInput {
  config: StrategyConfig;
  roster: StrategyDriver[];
  /** Stints already run, in order, as they really happened. */
  done: PlannedStint[];
  /**
   * The stint in the kart now, or about to start. `startMinute` is when it began
   * (or is expected to); `minLength` is how long it has already run, so it
   * cannot be planned to end in the past.
   */
  running: {
    startMinute: number;
    minLength: number;
    plannedMinutes: number;
    driverIndex: number | null;
    note?: string | null;
    /** The length was typed by hand: keep it (but never end before `minLength`). */
    pinned?: boolean;
  };
  /** The stints still to come, as planned. Their lengths set the profile. */
  tail: PlannedStint[];
}

/**
 * Re-plan the rest of a race from the point it has actually reached.
 *
 * The stop count is fixed by rule 10, so a stint that ran long (or short) can
 * only be paid for by the stints still to come. Their lengths are rescaled to
 * the driving time left, keeping the shape the plan already had, and each stop
 * is nudged clear of the closed pit windows exactly as the original layout did.
 * The final stint then carries whatever remains, so the race ends at the flag.
 *
 * Whoever was planned to drive each remaining stint still does; drivers are
 * only moved when a minimum can no longer be met any other way. Time already
 * driven counts towards those minimums.
 */
export function rebalanceTail(input: RebalanceInput): PlannedStint[] {
  const { config, roster, done, running, tail } = input;
  const c = deriveConstraints(config, driverCountOf(roster));
  const index = done.length;
  const count = tail.length + 1;
  const lo = c.planningFloorMinutes;
  const hi = c.maxStintMinutes;
  const runCap = index === 0 ? c.maxFirstStintMinutes : hi;
  const windows = pitWindowsOf(c);

  const driving = c.raceMinutes - running.startMinute - (count - 1) * c.minPitMinutes;

  // As in the original layout: the run to the flag must be long enough for the
  // stop before it to clear the end-of-race closure.
  const endClosure = c.planningPitWindows[c.planningPitWindows.length - 1];
  const finalStintFloor = Math.max(
    lo,
    c.raceMinutes - endClosure.fromMinute - c.minPitMinutes + 0.5,
  );

  const shape = [running.plannedMinutes, ...tail.map((t) => t.minutes)].map((m) =>
    Math.max(m, EPSILON),
  );
  // A pinned stint is somebody's decision, not part of the profile: it keeps its
  // length, and the others share what is left. The final stint is never pinned in
  // effect — it has to carry the remainder or the race does not end at the flag.
  const isPinned = (k: number): boolean =>
    k < count - 1 && (k === 0 ? running.pinned === true : tail[k - 1].pinned === true);
  const retarget = (from: number, left: number): number[] => {
    const rest = shape.map((_, k) => k).slice(from);
    const free = rest.filter((k) => !isPinned(k));
    const fixed = rest.filter(isPinned).reduce((sum, k) => sum + shape[k], 0);
    const shares = fillProportional(
      free.map((k) => shape[k]),
      left - fixed,
      lo,
      hi,
    );
    return rest.map((k) => (isPinned(k) ? shape[k] : shares[free.indexOf(k)]));
  };

  let targets = retarget(0, driving);
  const lengths: number[] = [];
  let elapsed = running.startMinute;

  for (let i = 0; i < count; i += 1) {
    const cap = i === 0 ? runCap : hi;
    const stintsAfter = count - i - 1;
    const budget = driving - lengths.reduce((sum, x) => sum + x, 0);

    let minutes: number;
    if (stintsAfter === 0) {
      minutes = budget;
    } else if (isPinned(i)) {
      // Honoured exactly, except that a stint in progress cannot end in the past.
      minutes = i === 0 ? Math.max(shape[0], running.minLength) : shape[i];
    } else {
      const reserve = (stintsAfter - 1) * lo + finalStintFloor;
      const upper = Math.floor(Math.min(cap, budget - reserve) + EPSILON);
      const lower = Math.ceil(
        Math.max(lo, budget - stintsAfter * hi, i === 0 ? running.minLength : 0) - EPSILON,
      );
      const want = Math.ceil(Math.min(Math.max(targets[0], lo), cap) - EPSILON);

      minutes =
        lengthClearOf(elapsed, want, lower, upper, windows) ??
        lengthClearOf(elapsed, want, lower, upper, c.closedPitWindows) ??
        Math.max(lower, Math.min(upper, want));
    }

    lengths.push(minutes);
    elapsed += minutes + (stintsAfter > 0 ? c.minPitMinutes : 0);

    if (stintsAfter > 0) {
      const spent = lengths.reduce((sum, x) => sum + x, 0);
      targets = retarget(i + 1, driving - spent);
    }
  }

  // Drivers. History is included in the arrays so its minutes count towards the
  // per-driver totals, but it is marked immutable. A driver the roster does not
  // know gets a unique negative id: it never matches `usable`, and it never
  // looks like the same person driving twice in a row.
  const unknown = (i: number): number => -(i + 1);
  const assigned: number[] = [
    ...done.map((s, i) => s.driverIndex ?? unknown(i)),
    running.driverIndex ?? unknown(index),
    ...tail.map((t, i) => t.driverIndex ?? unknown(index + 1 + i)),
  ];
  const allLengths = [...done.map((s) => s.minutes), ...lengths];
  const usable = availableIndexes(roster);
  const scope: RepairScope = {
    mutableFrom: index + 1,
    from: { index, startMinute: running.startMinute },
  };
  repairByReassignment(allLengths, assigned, c, usable, scope);
  repairByMinutes(allLengths, assigned, c, usable, scope);

  const kinds = stopKinds(assigned, c);
  const out: PlannedStint[] = done.map((s, i) => ({
    ...s,
    driverIndex: assigned[i] < 0 ? null : assigned[i],
    stopKind: kinds[i],
  }));

  let startMinute = running.startMinute;
  for (let k = 0; k < count; k += 1) {
    const at = index + k;
    const minutes = allLengths[at];
    const source = k === 0 ? null : tail[k - 1];
    out.push({
      startMinute,
      minutes,
      driverIndex: assigned[at] < 0 ? null : assigned[at],
      stopKind: kinds[at],
      note: source ? source.note : (running.note ?? null),
      ...((source ? source.pinned : running.pinned) && k < count - 1 ? { pinned: true } : {}),
    });
    startMinute += minutes + c.minPitMinutes;
  }
  return out;
}

export interface GenerateResult {
  stints: PlannedStint[];
  /** Non-empty when the roster or an override makes a legal plan impossible. */
  violations: Violation[];
}

/**
 * Lay out a legal plan for `config` and `roster`.
 *
 * Never throws: an impossible brief still returns the best plan it could build,
 * with a plan-level violation explaining what does not fit, so the sheet renders
 * and the user can see why.
 */
export function generatePlan(
  config: StrategyConfig,
  roster: StrategyDriver[],
): GenerateResult {
  const count = driverCountOf(roster);
  const c = deriveConstraints(config, count);
  const violations: Violation[] = [];

  if (count === 0) {
    const empty = planStintLengths(c, config);
    return {
      stints: toStints(
        empty,
        empty.map(() => null),
        empty.map((_, i) => (i === empty.length - 1 ? 'none' : 'driver-and-kart')),
        c.minPitMinutes,
      ),
      violations: rosterViolations(0, c, config.durationHours),
    };
  }

  violations.push(...rosterViolations(count, c, config.durationHours));

  const lengths = planStintLengths(c, config);
  const assigned = assignDrivers(
    lengths,
    c,
    roster,
    config.preferDoubleStints,
  ) as number[];

  // Reassignment first — it costs nothing and leaves the timeline alone. Only
  // when that cannot reach the minimums do we start moving minutes about.
  const usable = availableIndexes(roster);
  repairByReassignment(lengths, assigned, c, usable);
  const balanced = repairByMinutes(lengths, assigned, c, usable);

  if (!balanced) {
    violations.push({
      stintIndex: null,
      rule: '17',
      severity: 'error',
      message:
        'The per-driver minimum driving times cannot all be met with this roster. ' +
        'Mark another driver available, or reduce the squad so each driver owes less.',
      penalty: '—',
    });
  }

  return {
    stints: toStints(lengths, assigned, stopKinds(assigned, c), c.minPitMinutes),
    violations,
  };
}

/** Re-flow start times after an edit, so the sheet stays continuous. */
export function reflowStints(
  stints: PlannedStint[],
  minPitMinutes: number,
): PlannedStint[] {
  let startMinute = 0;
  return stints.map((s, i) => {
    const next = { ...s, startMinute };
    startMinute += s.minutes + (i < stints.length - 1 ? minPitMinutes : 0);
    return next;
  });
}

function round(minutes: number): number {
  return Math.round(minutes * 100) / 100;
}

/** Every way a plan can breach the regulations, with the penalty each carries. */
export function validatePlan(plan: StrategyPlan): Violation[] {
  const count = driverCountOf(plan.roster);
  const c = deriveConstraints(plan.config, count);
  const out: Violation[] = rosterViolations(count, c, plan.config.durationHours);
  const { stints } = plan;

  if (stints.length === 0) {
    out.push({
      stintIndex: null,
      rule: '10',
      severity: 'error',
      message: 'The plan has no stints yet — generate one.',
      penalty: null,
    });
    return out;
  }

  const stops = stints.length - 1;
  if (stops < c.requiredStops) {
    out.push({
      stintIndex: null,
      rule: '10',
      severity: 'error',
      message: `${stops} pit stops planned; ${plan.config.category} over ${plan.config.durationHours}h must make ${c.requiredStops}.`,
      penalty: `5 laps per missing stop (${c.requiredStops - stops} short)`,
    });
  }

  stints.forEach((s, i) => {
    const cap = i === 0 ? c.maxFirstStintMinutes : c.maxStintMinutes;

    if (s.minutes > cap + EPSILON) {
      const over = s.minutes - cap;
      out.push({
        stintIndex: i,
        rule: i === 0 ? '15' : '14',
        severity: 'error',
        message:
          i === 0
            ? `First stint is ${round(s.minutes)} min; ${plan.config.category} may run ${cap}.`
            : `Stint is ${round(s.minutes)} min, over the ${cap}-minute maximum.`,
        penalty: over <= 2 ? '20 seconds' : '1 lap',
      });
    }

    if (c.minStintMinutes > 0 && s.minutes < c.minStintMinutes - EPSILON) {
      const short = Math.ceil(c.minStintMinutes - s.minutes);
      out.push({
        stintIndex: i,
        rule: '13',
        severity: 'error',
        message: `Stint is ${round(s.minutes)} min, under the ${c.minStintMinutes}-minute minimum.`,
        penalty: `${short * 10} seconds (10s per started minute short)`,
      });
    }

    if (s.driverIndex === null) {
      out.push({
        stintIndex: i,
        rule: '10',
        severity: 'warning',
        message: 'No driver assigned to this stint.',
        penalty: null,
      });
    }

    // The last stint ends at the flag; it has no stop to check.
    if (i < stints.length - 1) {
      const pitIn = s.startMinute + s.minutes;
      const shut = closureAt(pitIn, c.closedPitWindows);
      if (shut) {
        out.push({
          stintIndex: i,
          rule: '16',
          severity: 'error',
          message: `Pits at ${formatOffset(pitIn)} while the lane is closed — ${shut.reason.toLowerCase()}.`,
          penalty: '3 laps',
        });
      } else {
        const tight = marginWarning(pitIn, i, c);
        if (tight) out.push(tight);
      }
    }
  });

  // Rule 11 — consecutive stints, and SILVER's swap-every-stop requirement.
  let run = 1;
  for (let i = 1; i < stints.length; i += 1) {
    const prev = stints[i - 1].driverIndex;
    const cur = stints[i].driverIndex;
    if (cur !== null && cur === prev) {
      run += 1;
      if (run > c.maxConsecutiveStints) {
        out.push({
          stintIndex: i,
          rule: '11',
          severity: 'error',
          message: c.mustChangeDriverEveryStop
            ? 'SILVER must change driver at every pit entry.'
            : `A driver may run at most ${c.maxConsecutiveStints} consecutive stints.`,
          penalty: '—',
        });
      }
    } else {
      run = 1;
    }
  }

  // Rule 17/18 — per-driver totals.
  summarisePlan(plan).driverTotals.forEach((d) => {
    if (d.minMinutes !== null && d.minutes < d.minMinutes - EPSILON) {
      out.push({
        stintIndex: null,
        rule: '17',
        severity: 'error',
        message: `${d.name} drives ${formatMinutes(d.minutes)}, under the ${formatMinutes(d.minMinutes)} minimum for a squad of ${count}.`,
        penalty: '—',
      });
    }
    if (d.maxMinutes !== null && d.minutes > d.maxMinutes + EPSILON) {
      out.push({
        stintIndex: null,
        rule: '18',
        severity: 'error',
        message: `${d.name} drives ${formatMinutes(d.minutes)}, over the ${formatMinutes(d.maxMinutes)} maximum for a squad of ${count}.`,
        penalty: '—',
      });
    }
  });

  const pressure = stopPressure(stints, c);
  if (pressure !== null) {
    if (pressure.slackMinutes < 0) {
      out.push({
        stintIndex: pressure.atStintIndex,
        rule: '16',
        severity: 'error',
        message: `${pressure.stopsRemaining} stops still to make after ${formatOffset(
          stints[pressure.atStintIndex].startMinute + stints[pressure.atStintIndex].minutes,
        )} — they do not fit before the lane shuts at ${formatOffset(pressure.closesAtMinute)}.`,
        penalty: '5 laps per stop not made',
      });
    } else if (
      pressure.stopsInFinalQuarter >=
      pressure.evenShareInFinalQuarter * LATE_STOP_CONCENTRATION
    ) {
      out.push({
        stintIndex: null,
        rule: '16',
        severity: 'warning',
        message:
          `${pressure.stopsInFinalQuarter} of ${stints.length - 1} stops fall after ` +
          `${formatOffset(pressure.finalQuarterFromMinute)}, against ${Math.round(
            pressure.evenShareInFinalQuarter,
          )} for an even plan. Every stop still owed late is a separate chance to be ` +
          `shut out by a safety kart.`,
        penalty: '5 laps per stop not made',
      });
    }
  }

  // The sheet must add up to the race, or the last stint is guesswork.
  const planned =
    stints.reduce((sum, s) => sum + s.minutes, 0) + stops * c.minPitMinutes;
  if (Math.abs(planned - c.raceMinutes) > 0.5) {
    out.push({
      stintIndex: null,
      rule: '3e',
      severity: planned < c.raceMinutes ? 'warning' : 'error',
      message: `The plan covers ${formatMinutes(planned)} of a ${formatMinutes(c.raceMinutes)} race.`,
      penalty: null,
    });
  }

  return out;
}

export function summarisePlan(plan: StrategyPlan): PlanSummary {
  const count = driverCountOf(plan.roster);
  const c = deriveConstraints(plan.config, count);

  const totals = new Map<number, { minutes: number; stints: number }>();
  plan.roster.forEach((_, i) => totals.set(i, { minutes: 0, stints: 0 }));

  plan.stints.forEach((s) => {
    if (s.driverIndex === null) return;
    const t = totals.get(s.driverIndex);
    if (!t) return;
    t.minutes += s.minutes;
    t.stints += 1;
  });

  const stops = Math.max(0, plan.stints.length - 1);
  const kartOnly = plan.stints.filter((s) => s.stopKind === 'kart-only').length;

  const driverTotals: DriverTotal[] = plan.roster.map((d, i) => {
    const t = totals.get(i) ?? { minutes: 0, stints: 0 };
    // An unavailable driver owes nothing — the bounds are for whoever races.
    const minMinutes = d.unavailable ? null : c.minDriverMinutes;
    const maxMinutes = d.unavailable ? null : c.maxDriverMinutes;
    return {
      name: d.name,
      driverIndex: i,
      minutes: t.minutes,
      stints: t.stints,
      minMinutes,
      maxMinutes,
      shortfallMinutes:
        minMinutes === null ? 0 : Math.max(0, minMinutes - t.minutes),
    };
  });

  const totalDrivingMinutes = plan.stints.reduce((sum, s) => sum + s.minutes, 0);
  const totalPitMinutes = stops * c.minPitMinutes;

  return {
    driverTotals,
    totalDrivingMinutes,
    totalPitMinutes,
    plannedMinutes: totalDrivingMinutes + totalPitMinutes,
    stops,
    driverChanges: plan.stints.filter((s) => s.stopKind === 'driver-and-kart').length,
    kartOnlyStops: kartOnly,
    constraints: c,
  };
}

/** "1:05:30" from minutes-since-start, for the sheet's Start/End columns. */
export function formatOffset(minutes: number): string {
  const total = Math.round(minutes * 60);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/**
 * "2:45" — minutes and seconds, never rounded.
 *
 * Pit stops are announced to the second (135s at Haskovo, 165s in Slovakia) and
 * being a second under the minimum is a 15-second penalty, so showing a
 * 2:45 stop as "3m" would misstate the one number a driver has to hit.
 */
export function formatMinutesSeconds(minutes: number): string {
  const total = Math.round(minutes * 60);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** "2h 05m" — for driving totals, where seconds are noise. */
export function formatMinutes(minutes: number): string {
  const whole = Math.round(minutes);
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m}m`;
}

/** Wall-clock time of a point in the race, given the config's start. */
export function clockAt(startsAt: string, minutes: number): string {
  const base = Date.parse(startsAt);
  if (!Number.isFinite(base)) return formatOffset(minutes);
  const at = new Date(base + minutes * 60_000);
  return `${at.getHours().toString().padStart(2, '0')}:${at
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
}
